// A step's read-only body on the survey page (redesign spec §7): what a
// guest sees, compactly — a choice step's options with their icons (and
// values when it's the score), a multi-select's options (split by score
// into its low and high sets), a text step's placeholder.

import type { ReactNode } from "react";
import type { StepRow } from "@/api/types";
import { builtinIconSvg } from "../choice-icons";
import { type MultiOptionFormValue, type OptionFormValue, toStepFormValue } from "../survey-schema";

function Chip({ children }: { children: ReactNode }) {
    return (
        <li className="inline-flex items-center gap-1.5 rounded-kore-md border border-kore-border px-2 py-1 text-sm">
            {children}
        </li>
    );
}

function ChipRow({ children }: { children: ReactNode }) {
    return <ul className="flex flex-wrap gap-2">{children}</ul>;
}

function OptionIcon({ option }: { option: OptionFormValue }) {
    if (option.assetUrl) return <img src={option.assetUrl} alt="" className="size-5 object-contain" />;
    const svg = option.icon ? builtinIconSvg(option.icon) : undefined;
    if (!svg) return null;
    // biome-ignore lint/security/noDangerouslySetInnerHtml: built-in icon SVGs are bundled constants, not user input
    return <span aria-hidden="true" className="size-5 [&>svg]:size-5" dangerouslySetInnerHTML={{ __html: svg }} />;
}

function MultiChips({ options }: { options: MultiOptionFormValue[] }) {
    return (
        <ChipRow>
            {options.map((option) => (
                <Chip key={option.key}>{option.label || option.key}</Chip>
            ))}
        </ChipRow>
    );
}

export function StepSummary({ step }: { step: StepRow }) {
    const value = toStepFormValue(step);
    if (value.kind === "choice") {
        return (
            <ChipRow>
                {value.options.map((option) => (
                    <Chip key={option.key}>
                        <OptionIcon option={option} />
                        {option.label || option.key}
                        {value.isMetric && <span className="text-kore-text-muted"> · {option.value}</span>}
                    </Chip>
                ))}
            </ChipRow>
        );
    }
    if (value.kind === "multi") {
        if (value.multiMode === "flat") return <MultiChips options={value.multiOptions} />;
        return (
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                    <span className="text-sm text-kore-text-muted">Low score (≤ {value.threshold})</span>
                    <MultiChips options={value.negative} />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-sm text-kore-text-muted">High score</span>
                    <MultiChips options={value.positive} />
                </div>
            </div>
        );
    }
    return <p className="text-sm text-kore-text-muted">{value.placeholder || "Free text"}</p>;
}
